/**
 * IDLEMILE - Game Balance Configuration
 *
 * All game balance constants in one place.
 * Modify these to tune gameplay without touching logic code.
 *
 * Organization:
 * - ITEM_BALANCE: Rarity weights, stat ranges, star ratings
 * - SPAWN_BALANCE: Item spawn rates, roadside density
 * - WAVE_BALANCE: Wave timing, spawn rates, difficulty progression
 * - LOT_BALANCE: Pack contents, quality distributions
 * - PRESTIGE_BALANCE: Prestige bonuses, tier requirements
 */

// =============================================================================
// ITEM BALANCE
// =============================================================================

/**
 * Tier-based weight tables for item rarity distribution in packs.
 *
 * VERY CONSERVATIVE DROP RATES: Fusion is the primary upgrade path
 *
 * Design philosophy:
 * - Drops almost always match lot tier (~92-97%)
 * - Rare chance of +1 tier upgrade (~3-7%)
 * - Very rare chance of +2 tier upgrade (~1%)
 * - Encourages fusion as the main way to get higher tier items
 */
export const ITEM_TIER_WEIGHTS = {
    // Faded packs (quality < 0.2): Mostly faded drops
    faded: {
        faded: 92,
        standard: 7,
        pristine: 1,
        golden: 0,
        vectorized: 0
    },
    // Standard packs (quality 0.2-0.4): Mostly standard
    standard: {
        faded: 0,
        standard: 93,
        pristine: 6,
        golden: 1,
        vectorized: 0
    },
    // Pristine packs (quality 0.4-0.6): Mostly pristine
    pristine: {
        faded: 0,
        standard: 0,
        pristine: 94,
        golden: 5,
        vectorized: 1
    },
    // Golden packs (quality 0.6-0.8): Mostly golden
    golden: {
        faded: 0,
        standard: 0,
        pristine: 0,
        golden: 97,
        vectorized: 3
    },
    // Vectorized packs (quality >= 0.8): Mostly vectorized with some golden
    vectorized: {
        faded: 0,
        standard: 0,
        pristine: 0,
        golden: 10,
        vectorized: 90
    }
};

/**
 * Maximum number of items per pack that can "upgrade" above the lot tier.
 * Prevents multiple lucky drops in a single pack.
 */
export const MAX_UPGRADES_PER_PACK = 1;

/**
 * Rarity tier indices for calculations.
 * Higher index = rarer tier.
 */
export const RARITY_TIER_INDEX = {
    faded: 0,
    standard: 1,
    pristine: 2,
    golden: 3,
    vectorized: 4
};

/**
 * Rarity name prefixes for item display names.
 * Standard has no prefix (empty string).
 */
export const RARITY_PREFIXES = {
    faded: 'Faded',
    standard: '',  // No prefix for standard items
    pristine: 'Pristine',
    golden: 'Golden',
    vectorized: 'Vectorized'
};

/**
 * Rarity colors for UI display (hex).
 * Also available in public/data/items.json for runtime access.
 */
export const RARITY_COLORS = {
    faded: '#888888',
    standard: '#4a9eff',
    pristine: '#a335ee',
    golden: '#ff8000',
    vectorized: '#e6cc80'
};

/**
 * Quality thresholds for determining pack/wave tier from quality value (0.0-1.0).
 * Used by getPackTier(), getWaveTier(), etc.
 */
export const QUALITY_TIER_THRESHOLDS = {
    vectorized: 0.8,  // quality >= 0.8
    golden: 0.6,      // quality >= 0.6
    pristine: 0.4,    // quality >= 0.4
    standard: 0.2     // quality >= 0.2
    // Below 0.2 = faded
};

// =============================================================================
// WAVE BALANCE
// =============================================================================

/**
 * Wavedust bonus amounts for opening waves by tier.
 * Wavedust is the premium currency earned from opening lot packs.
 */
export const WAVE_OPEN_WAVEDUST = {
    faded: 3,
    standard: 8,
    pristine: 15,
    golden: 22,
    vectorized: 30
};

/**
 * Lot opening cost (wavedust) - scales gently per tier.
 * Costs are lower than wavedust gained from opening + scrapping to ensure positive flow:
 * - Opening gives 3-30 wavedust bonus
 * - Scrapping unpicked items gives additional wavedust
 * - Cost of 5-25 means opening is affordable and sustainable
 */
export const LOT_OPEN_COST = {
    faded: 5,
    standard: 10,
    pristine: 15,
    golden: 20,
    vectorized: 25
};

/**
 * Wave spawn distance configuration.
 * Controls how far apart waves spawn on the road.
 */
export const WAVE_SPAWN_DISTANCE = {
    // Base distance range between waves (miles)
    MIN_DISTANCE: 70,
    MAX_DISTANCE: 150,

    // Quality scaling based on distance
    // Longer wait = slightly higher quality
    BASE_QUALITY: 0.12,
    QUALITY_PER_MILE: 0.0025,  // (distance - 70) * this value

    // Random quality variance (+/-)
    QUALITY_VARIANCE: 0.08
};

/**
 * Pack cost calculation parameters.
 * Cost scales with quality and progression.
 * @deprecated Use LOT_OPEN_COST for wavedust-based lot costs instead.
 * This miles-based cost formula is kept for backwards compatibility.
 */
export const PACK_COST = {
    // Base cost at lowest quality
    BASE_COST: 25,

    // Additional cost scaled by quality squared
    // At max quality: BASE_COST + (1.0 * 1.0 * QUALITY_SCALING) = ~300
    QUALITY_SCALING: 275,

    // Small progression multiplier based on total miles
    // 0.5% increase per 100 miles driven
    PROGRESSION_RATE: 0.00005
};

// =============================================================================
// LOT/PACK BALANCE
// =============================================================================

/**
 * Number of items generated per pack.
 * Currently fixed at 3 items per pack.
 */
export const ITEMS_PER_PACK = 3;

/**
 * Type-specific lot configuration.
 * 25% of wave pickups are slot-focused lots containing items from a single slot.
 * This reduces frustration from bad RNG by allowing targeted item farming.
 */
export const TYPE_SPECIFIC_LOT = {
    // Chance that a wave pickup is type-specific (25%)
    RATE: 0.25,

    // Valid slot types for type-specific lots
    SLOT_TYPES: ['rearview', 'dashboard', 'keychain']
};

// =============================================================================
// PRESTIGE BALANCE
// =============================================================================

/**
 * Prestige bonus calculation rates.
 * Target progression:
 * - ~10% at 10k miles
 * - ~25% at 50k miles
 * - ~40% at 100k miles
 */
export const PRESTIGE_RATES = {
    // Base bonus rate: 0.0005% per 100 totalMiles
    BASE_BONUS_RATE: 0.000005,

    // Extra bonus rate: 0.00025% per 100 unspent miles
    EXTRA_BONUS_RATE: 0.0000025,

    // Level multiplier increase: +15% more generous per prestige level
    LEVEL_MULTIPLIER_INCREASE: 0.15
};

/**
 * Prestige tier thresholds and names.
 * Based on prestige level count.
 */
export const PRESTIGE_TIERS = {
    // Level thresholds for tier names
    THRESHOLDS: {
        bronze: 1,      // Level 1-2
        silver: 3,      // Level 3-5
        gold: 6,        // Level 6-10
        platinum: 11,   // Level 11-20
        diamond: 21,    // Level 21-50
        transcendent: 51
    },

    // Tier colors (hex)
    COLORS: {
        none: '#888888',
        bronze: '#cd7f32',
        silver: '#c0c0c0',
        gold: '#ffd700',
        platinum: '#e5e4e2',
        diamond: '#b9f2ff',
        transcendent: '#ff6ec7'
    }
};

// =============================================================================
// CAR TIER BALANCE
// =============================================================================

/**
 * Car quality tier configuration.
 *
 * Tiers represent the visual/stat quality of a car based on miles invested.
 * Higher tiers provide stat multipliers and better upgrade transfer rates.
 */
export const CAR_TIER_BALANCE = {
    // Tier order (lowest to highest quality)
    TIER_ORDER: ['faded', 'standard', 'pristine', 'golden', 'vectorized'],

    // Miles needed to reach each tier (starter car baseline)
    // Better cars multiply these thresholds by CAR_TIER_SCALING
    BASE_TIER_THRESHOLDS: [0, 200, 600, 1500, 3500],

    // Stat multiplier per tier (applied to all car stats)
    TIER_STAT_MULTIPLIERS: {
        faded: 1.0,
        standard: 1.05,
        pristine: 1.12,
        golden: 1.20,
        vectorized: 1.30
    },

    // Upgrade level transfer rate per tier (when ascending to new car)
    // Higher tier = more progress carries over
    TIER_TRANSFER_RATES: {
        faded: 0.20,
        standard: 0.40,
        pristine: 0.60,
        golden: 0.80,
        vectorized: 1.00
    },

    // Tier colors (matching item rarity colors)
    TIER_COLORS: {
        faded: '#888888',
        standard: '#4a9eff',
        pristine: '#a335ee',
        golden: '#ff8000',
        vectorized: '#e6cc80'
    },

    // Miles lost during car ascension (25%)
    ASCENSION_LOSS: 0.25,

    // Maximum upgrade level (tier no longer gates upgrades)
    MAX_UPGRADE_LEVEL: 10
};

/**
 * Car tier scaling multipliers.
 *
 * Better cars need more miles invested to reach the same tier.
 * Multiply BASE_TIER_THRESHOLDS by this value for each car.
 */
export const CAR_TIER_SCALING = {
    starter: 1.0,
    sedan: 1.3,
    sports: 1.6,
    supercar: 2.0,
    hypercar: 2.5
};

/**
 * Car upgrade cost multipliers.
 *
 * Better cars have higher upgrade costs.
 * Multiply base upgrade costs by this value.
 */
export const CAR_UPGRADE_COST_MULTIPLIERS = {
    starter: 1,
    sedan: 2,
    sports: 3,
    supercar: 4.5,
    hypercar: 6
};

// =============================================================================
// SALVAGE CRATE BALANCE
// =============================================================================

/**
 * Salvage crate pickup configuration.
 *
 * Salvage crates are auto-collected pickups that award wavedust directly.
 * They ensure players always have wavedust flowing to fund lot opening.
 */
export const SALVAGE_CRATE_BALANCE = {
    // Base wavedust award from a salvage crate
    BASE_WAVEDUST: 22,

    // Quality multiplier: wavedust = base * (1 + quality * QUALITY_SCALE)
    // At quality 0.5: 22 * (1 + 0.5 * 0.5) = 22 * 1.25 = 27.5
    // At quality 1.0: 22 * (1 + 1.0 * 0.5) = 22 * 1.5 = 33
    QUALITY_SCALE: 0.5,

    // Random variance: ±35% (final amount is multiplied by 0.65-1.35)
    VARIANCE: 0.35
};
